// src/pages/SupplyHistory.jsx - メインコンポーネント（分割版）
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Card, CardContent, Button, IconButton,
  TablePagination, Alert, CircularProgress
} from '@mui/material';

import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import TableChartIcon from '@mui/icons-material/TableChart';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

// 🚀 分割されたコンポーネントをインポート
import Header from "../../components/Header";
import SupplyEditDialog from './SupplyEditDialog';
import SupplyApiService from './supplyList/SupplyApiService';
import SupplyFilterSection from './supplyList/SupplyFilterSection';
import SupplyListTable from './supplyList/SupplyListTable';
import SupplyCardView from './supplyList/SupplyCardView'; // 後で作成
import { useSupplyFilters } from './supplyList/useSupplyFilters'; // カスタムフック（後で作成）

// Boolean型安全変換ユーティリティ
import { ensureBooleanForAPI } from '../../utils/booleanUtils';

/**
 * 🚀 供給履歴メインコンポーネント
 * 分割により保守性と可読性を大幅改善
 */
const SupplyHistory = () => {
  // 🔹 基本状態管理
  const [supplies, setSupplies] = useState([]);
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // 🔹 UI状態管理
  // ✅ 修正: デフォルトで絞り込みを閉じた状態に変更
  const [showFilter, setShowFilter] = useState(false); // true → false に変更
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'card'
  const [page, setPage] = useState(0);
  const rowsPerPage = 10;
  
  // 🔹 ダイアログ状態管理
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedSupply, setSelectedSupply] = useState(null);
  
  // 🔹 フィルター状態管理（カスタムフック使用）
  const {
    clientFilter,
    setClientFilter,
    productFilter,
    setProductFilter,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    resetFilters,
    getFilterParams
  } = useSupplyFilters();

  // 🚀 データ取得（フィルタリング対応）
  const fetchData = useCallback(async (filters = {}) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await SupplyApiService.fetchInitialData(filters);
      
      if (result.success) {
        setSupplies(result.supplies);
        setClients(result.clients);
        setProducts(result.products);
      } else {
        throw new Error(result.error || 'データ取得に失敗しました');
      }
      
    } catch (err) {
      setError(err.message);
      // エラー時も空配列をセット（画面を壊さない）
      setSupplies([]);
      setClients([]);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // 🚀 フィルター適用（バックエンドで処理）
  const handleApplyFilter = useCallback(async () => {
    const filterParams = getFilterParams();
    await fetchData(filterParams);
    setPage(0); // ページをリセット
  }, [fetchData, getFilterParams]);

  // 🚀 フィルターリセット
  const handleResetFilter = useCallback(async () => {
    resetFilters();
    await fetchData(); // フィルターなしで全件取得
    setPage(0);
  }, [fetchData, resetFilters]);

  // 🚀 ソート処理（フロントエンドで実行）
  const sortSupplies = useCallback((key, order = 'asc') => {
    try {
      const sorted = [...supplies].sort((a, b) => {
        let aVal = a[key];
        let bVal = b[key];
        
        // 日付の場合は適切にソート
        if (key === 'supplyDate') {
          aVal = new Date(aVal);
          bVal = new Date(bVal);
        }
        
        if (aVal < bVal) return order === 'asc' ? -1 : 1;
        if (aVal > bVal) return order === 'asc' ? 1 : -1;
        return 0;
      });
      
      setSupplies(sorted);
    } catch (err) {
      setError("ソート処理でエラーが発生しました");
    }
  }, [supplies]);

  // 🔹 ページング用データ
  const displayedSupplies = useMemo(() => {
    if (!Array.isArray(supplies)) return [];
    return supplies.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [supplies, page, rowsPerPage]);

  // 🔹 初期データ取得
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // 🚀 更新処理
  const handleUpdate = useCallback(async () => {
    if (!selectedSupply) return;
    
    setLoading(true);
    
    try {
      // 🚀 修正：Boolean値の安全な変換
      const updateData = {
        amount: selectedSupply.amount,
        supplyDate: selectedSupply.supplyDate,
        isExceptional: ensureBooleanForAPI(selectedSupply.isExceptional),
        clientProductId: selectedSupply.clientProductId
      };
      
      // 後方互換性
      if (!updateData.clientProductId && selectedSupply.clientId) {
        updateData.clientId = selectedSupply.clientId;
      }
      
      await SupplyApiService.updateSupply(selectedSupply.id, updateData);
      
      alert("更新しました");
      
      // データ再取得
      const filterParams = getFilterParams();
      await fetchData(filterParams);
      
    } catch (err) {
      if (err.message.includes('404')) {
        alert("この供給履歴はすでに削除されています。");
      } else if (err.message.includes('401')) {
        alert("認証が切れました。再ログインしてください。");
      } else {
        alert("更新に失敗しました。もう一度お試しください。");
      }
    } finally {
      setLoading(false);
      setOpenDialog(false);
    }
  }, [selectedSupply, fetchData, getFilterParams]);

  // 🚀 削除処理
  const handleDelete = useCallback(async () => {
    if (!selectedSupply) return;
    
    setLoading(true);
    
    try {
      await SupplyApiService.deleteSupply(selectedSupply.id);
      
      alert("削除しました");
      
      // データ再取得
      const filterParams = getFilterParams();
      await fetchData(filterParams);
      
    } catch (err) {
      if (err.message.includes('404')) {
        alert("この供給履歴はすでに削除されています。");
      } else if (err.message.includes('401')) {
        alert("認証が切れました。再ログインしてください。");
      } else {
        alert("削除に失敗しました。もう一度お試しください。");
      }
    } finally {
      setLoading(false);
      setOpenDialog(false);
    }
  }, [selectedSupply, fetchData, getFilterParams]);

  // 🔹 イベントハンドラー
  const handleChangePage = useCallback((event, newPage) => {
    setPage(newPage);
  }, []);

  const handleRowClick = useCallback((supply) => {
    setSelectedSupply(supply);
    setOpenDialog(true);
  }, []);

  const handleChangeSupply = useCallback((updated) => {
    setSelectedSupply(updated);
  }, []);

  // エラー表示
  if (error) {
    return (
      <>
        <Header />
        <Box sx={{ maxWidth: 'md', mx: 'auto', px: 2, py: 3 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Button 
            variant="contained" 
            onClick={() => {
              setError(null);
              fetchData();
            }}
          >
            再試行
          </Button>
        </Box>
      </>
    );
  }

  // ローディング表示（初回のみ）
  if (loading && supplies.length === 0) {
    return (
      <>
        <Header />
        <Box sx={{ maxWidth: 'md', mx: 'auto', px: 2, py: 3, textAlign: 'center' }}>
          <CircularProgress />
          <Typography sx={{ mt: 2 }}>データを読み込み中...</Typography>
        </Box>
      </>
    );
  }

  return (
    <>
      <Header />
      <Box sx={{ 
        maxWidth: 'md', 
        mx: 'auto', 
        px: 2, 
        py: 3, 
        // PWAボトムナビゲーション対応：安全マージンを確保
        pb: { xs: 12, sm: 6 }, // モバイルは12（約96px）、デスクトップは6
        // 最小高さを確保してスクロール可能にする
        minHeight: { xs: 'calc(100vh - 200px)', sm: 'auto' }
      }}>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            
            {/* 🔹 ヘッダーエリア */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              mb: 2,
              flexWrap: { xs: 'wrap', sm: 'nowrap' },
              gap: 1
            }}>
              <Typography variant="h6" sx={{ 
                fontWeight: 500, 
                whiteSpace: 'nowrap',
                width: 'auto'
              }}>
                供給履歴一覧 
                {loading && <CircularProgress size={16} sx={{ ml: 1 }} />}
              </Typography>
              
              {/* 表示切替コントロール */}
              <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={showFilter ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                  onClick={() => setShowFilter(!showFilter)}
                >
                  {showFilter ? '絞り込みを隠す' : '絞り込みを表示'}
                </Button>
                
                <Box sx={{ display: 'flex', border: 1, borderColor: 'divider', borderRadius: 1 }}>
                  <IconButton 
                    onClick={() => setViewMode('table')}
                    sx={{ 
                      p: 1, 
                      bgcolor: viewMode === 'table' ? 'primary.50' : 'transparent',
                      color: viewMode === 'table' ? 'primary.main' : 'inherit'
                    }}
                  >
                    <TableChartIcon fontSize="small" />
                  </IconButton>
                  <IconButton 
                    onClick={() => setViewMode('card')}
                    sx={{ 
                      p: 1,
                      bgcolor: viewMode === 'card' ? 'primary.50' : 'transparent',
                      color: viewMode === 'card' ? 'primary.main' : 'inherit'
                    }}
                  >
                    <ViewModuleIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            </Box>
            
            {/* 🚀 フィルターセクション（分離されたコンポーネント） */}
            <SupplyFilterSection
              showFilter={showFilter}
              supplies={supplies}
              clientFilter={clientFilter}
              setClientFilter={setClientFilter}
              productFilter={productFilter}
              setProductFilter={setProductFilter}
              startDate={startDate}
              setStartDate={setStartDate}
              endDate={endDate}
              setEndDate={setEndDate}
              onApplyFilter={handleApplyFilter}
              onResetFilter={handleResetFilter}
              loading={loading}
            />
            
            {/* 🔹 ソートボタン */}
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 2 }}>
              <Button 
                variant="outlined" 
                size="small"
                startIcon={<ArrowDownwardIcon />}
                onClick={() => sortSupplies('supplyDate', 'desc')}
                disabled={loading}
              >
                日付↓
              </Button>
              <Button 
                variant="outlined" 
                size="small"
                startIcon={<ArrowUpwardIcon />}
                onClick={() => sortSupplies('supplyDate', 'asc')}
                disabled={loading}
              >
                日付↑
              </Button>
            </Box>
            
            {/* 🚀 表示エリア（コンポーネント分離） */}
            {viewMode === 'table' ? (
              <SupplyListTable 
                supplies={displayedSupplies}
                onRowClick={handleRowClick}
                loading={loading}
              />
            ) : (
              <SupplyCardView 
                supplies={displayedSupplies}
                onCardClick={handleRowClick}
                loading={loading}
              />
            )}
            
            {/* 🔹 ページネーション */}
            <TablePagination
              component="div"
              count={supplies.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[10]}
              labelRowsPerPage="表示件数:"
            />
          </CardContent>
        </Card>
      </Box>

      {/* 🔹 編集ダイアログ */}
      <SupplyEditDialog
        open={openDialog}
        supply={selectedSupply}
        onClose={() => setOpenDialog(false)}
        onChange={handleChangeSupply}
        onUpdate={handleUpdate}
        onDelete={handleDelete}
        clients={clients}
        products={products}
      />
    </>
  );
};

export default SupplyHistory;